// =====================================================================
// REGLAS DE PANTALLA DE LA IDENTIDAD VERIFICABLE · ESTABILIDAD Parte C
// =====================================================================
// Funciones puras, sin React, para probarlas en Node sin montar nada. Son
// LAS MISMAS reglas que aplica el servidor (`backend/app/seguridad/
// identidad/rfc_curp.py`), dichas mientras se escribe para no descubrir
// al guardar que faltaba un carácter. El servidor es la fuente de verdad.

/** Los pasos del asistente del Admin Supremo, en orden. */
export const PASOS = ['Qué es', 'Tu teléfono', 'RFC de tu empresa', 'Tu CURP', 'Tu Constancia'];

/** Mayúsculas y sin espacios, guiones ni puntos: se autocorrige al escribir. */
export function normalizarClave(v) {
  return (v || '').replace(/[\s.-]/g, '').toUpperCase();
}

export function soloDigitos(v, max = 6) {
  return (v || '').replace(/\D/g, '').slice(0, max);
}

const VALORES_RFC = '0123456789ABCDEFGHIJKLMN&OPQRSTUVWXYZ Ñ';
const VALORES_CURP = '0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ';
const ESTADOS_CURP = new Set(
  'AS BC BS CC CL CM CS CH DF DG GT GR HG JC MC MN MS NT NL OC PL QT QR SP SL SR TC TS TL VZ YN ZS NE'.split(' '),
);

function fechaValida(aa, mm, dd, anioBase) {
  const anio = anioBase + Number(aa);
  const f = new Date(Date.UTC(anio, Number(mm) - 1, Number(dd)));
  return f.getUTCFullYear() === anio && f.getUTCMonth() === Number(mm) - 1
    && f.getUTCDate() === Number(dd);
}

export function digitoRfc(rfc) {
  let base = rfc.slice(0, -1);
  if (base.length === 11) base = ` ${base}`;
  let suma = 0;
  for (let i = 0; i < base.length; i += 1) suma += VALORES_RFC.indexOf(base[i]) * (13 - i);
  const resto = suma % 11;
  if (resto === 0) return '0';
  const d = 11 - resto;
  return d === 10 ? 'A' : String(d);
}

export function digitoCurp(curp) {
  let suma = 0;
  for (let i = 0; i < 17; i += 1) suma += VALORES_CURP.indexOf(curp[i]) * (18 - i);
  const d = 10 - (suma % 10);
  return d === 10 ? '0' : String(d);
}

/** Qué le falta a este RFC, en una frase; null si está bien. */
export function problemaRfc(v) {
  const rfc = normalizarClave(v);
  if (!rfc) return 'Escribe el RFC. Lo encuentras arriba, en tu Constancia de Situación Fiscal.';
  if (rfc === 'XAXX010101000' || rfc === 'XEXX010101000') {
    return 'Ese es el RFC genérico de ventas al público, no el de una empresa.';
  }
  const m = /^([A-ZÑ&]{3,4})(\d{2})(\d{2})(\d{2})([A-Z0-9]{2})([0-9A])$/.exec(rfc);
  if (!m) return `Van ${rfc.length} caracteres: un RFC de empresa lleva 12 y el de una persona 13.`;
  if (!fechaValida(m[2], m[3], m[4], 2000) && !fechaValida(m[2], m[3], m[4], 1900)) {
    return 'La fecha dentro del RFC (los seis números) no existe. Revisa esos números.';
  }
  if (digitoRfc(rfc) !== rfc[rfc.length - 1]) {
    return 'El último carácter no corresponde con los demás: revisa que esté copiado igual que en tu Constancia.';
  }
  return null;
}

/** Qué le falta a esta CURP, en una frase; null si está bien. */
export function problemaCurp(v) {
  const curp = normalizarClave(v);
  if (!curp) return 'Escribe tu CURP: son 18 caracteres y vienen en tu credencial para votar.';
  const m = /^([A-Z][AEIOUX][A-Z]{2})(\d{2})(\d{2})(\d{2})([HMX])([A-Z]{2})([B-DF-HJ-NP-TV-Z]{3})([A-Z0-9])(\d)$/.exec(curp);
  if (!m) return `Van ${curp.length} de 18 caracteres. Revisa que esté completa y sin errores.`;
  if (!ESTADOS_CURP.has(m[6])) return `«${m[6]}» no es la clave de ningún estado (letras 12 y 13).`;
  const base = /[A-Z]/.test(m[8]) ? 2000 : 1900;
  if (!fechaValida(m[2], m[3], m[4], base)) return 'La fecha de nacimiento dentro de la CURP no existe.';
  if (digitoCurp(curp) !== curp[17]) {
    return 'El último número no corresponde con los demás: revisa que esté copiada igual que en tu credencial.';
  }
  return null;
}

/** Diez dígitos, quitando el +52 y el 1: igual que el servidor. */
export function telefonoDiez(v) {
  let d = (v || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('52')) d = d.slice(2);
  else if (d.length === 13 && d.startsWith('521')) d = d.slice(3);
  return d;
}

export function problemaTelefono(v) {
  const d = telefonoDiez(v);
  if (d.length !== 10) return `Van ${d.length} de 10 dígitos, con la clave de tu ciudad.`;
  if (d[0] === '0' || d[0] === '1') return 'Un teléfono de 10 dígitos no empieza con 0 ni con 1.';
  if (d.startsWith('800') || d.startsWith('900')) return 'Los 800 y 900 son de empresas, no de una persona.';
  return null;
}

/**
 * ¿Se le abre el asistente de identidad al entrar?
 *
 * SOLO si el servidor dice que está ENCENDIDA y que a esta persona le es
 * OBLIGATORIA. Con el aviso de privacidad en borrador el servidor dice
 * `encendida: false`, y esto devuelve false: ninguna pantalla pide nada.
 */
export function debeVerAsistenteIdentidad(usuario, estado) {
  return !!usuario
    && !usuario.debe_cambiar_password
    && usuario.correo_verificado !== false
    && !!estado
    && estado.encendida === true
    && estado.obligatoria === true
    && estado.estado === 'PENDIENTE';
}

/** El primer paso que falta (1..4), o 1 si no hay estado. */
export function primerPasoPendiente(estado) {
  const p = estado?.pasos || {};
  if (!p.telefono?.hecho) return 1;
  if (!p.rfc?.hecho) return 2;
  if (!p.curp?.hecho) return 3;
  if (!p.csf?.hecho) return 4;
  return 4;
}
